'use client'
import ScreenLoader from '@/app/[lng]/components/Skeletons/ScreenLoader'
import { useTranslation } from '@/app/i18n/client'
import { DictType } from '@/app/types/constantDataType'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { useGetRequestNewsMutation } from '@/app/globalRedux/api/NonLoggedIn/GuestNewsEndPoints'
import React, { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'sonner'
import {
  initialState,
  setGuestNewsForm,
} from '@/app/globalRedux/guestNewsForm/GuestNewsFormSlice'
import { RootState } from '@/app/globalRedux/store'
import GuestNewsFormPage from './GuestNewsForm'
import { GuestNewsFormUtils } from '@/app/utils/GuestNewsForm'
import ArticleIcon from '@mui/icons-material/Article'
import { useParams, useRouter } from 'next/navigation'
import HCaptcha from '@hcaptcha/react-hcaptcha'
import { CaptchaValidator } from '@/app/hooks/useCaptcha'
import ReCAPTCHA from 'react-google-recaptcha'
import FeatureFlagCaptcha from '@/app/[lng]/components/Validation/FeatureFlagCaptcha'

const GuestNewsModal = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const router = useRouter()
  const params = useParams<{ lng: string }>()
  const captchaRef = useRef<HCaptcha | ReCAPTCHA | null>(null)
  const { validateCaptcha } = CaptchaValidator(captchaRef)

  const [requestNews, { isLoading }] = useGetRequestNewsMutation()
  const [open, setOpen] = useState<boolean>(false)
  const guestNewsState = useSelector((state: RootState) => state.GuestNewsForm)
  const isLoggedIn = useSelector((state: RootState) => state.user.loggedIn)
  const guestNewsFormUtils = new GuestNewsFormUtils(guestNewsState)

  const submitNews = async () => {
    dispatch(setGuestNewsForm({ isFormSubmitted: true }))
    if (guestNewsFormUtils?.isFormValid()) {
      const isCaptchaVerified = await validateCaptcha()
      if (!isCaptchaVerified) return

      const formData = await guestNewsFormUtils.formDataForRequestBody()
      try {
        const response = await requestNews(formData).unwrap()

        if (response?.status == 'success') {
          toast.success(t('News submitted successfully'))
          setOpen(false)
          dispatch(setGuestNewsForm(initialState))
        } else {
          toast.error(response?.status)
        }
      } catch (error: any) {
        toast.error(error?.data?.message)
      }
    } else {
      toast.warning(t('Please fill all the required fields'))
    }
  }
  const handleOpenForm = (e: any) => {
    if (isLoggedIn) {
      e.preventDefault()
      router.push(`/${params?.lng}/myfructidor/news`)
    }
  }
  const newsBtn: DictType = {
    true: 'Creating News',
    false: 'SUBMIT',
  }

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <div className="flex">
          <DialogTrigger asChild>
            <Button
              className="w-full flex gap-3 text-[15px] items-center font-semibold"
              variant="secondary"
              onClick={handleOpenForm}
            >
              {t('POST YOUR NEWS')}
              <ArticleIcon />
            </Button>
          </DialogTrigger>
        </div>
        <DialogContent
          className="border-0 p-0 !max-w-[1080px] bg-[white] max-h-[90vh]"
          onInteractOutside={(e: any) => {
            e.preventDefault()
          }}
        >
          <DialogHeader>
            <DialogTitle className="bg-modal_header text-[15px] px-8 py-4 tracking-wide flex items-center gap-8">
              {t('POST YOUR NEWS')}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-scroll max-h-[70vh]">
            <GuestNewsFormPage />
            <div className=" pb-5 px-8">
              <FeatureFlagCaptcha ref={captchaRef} />
            </div>

            <DialogFooter>
              <div className="flex gap-x-3">
                <Button
                  variant="secondary"
                  size="sx"
                  onClick={() => {
                    submitNews()
                  }}
                  disabled={guestNewsState?.isFormSubmitted && isLoading}
                >
                  {t(
                    newsBtn[
                      String(guestNewsState?.isFormSubmitted && isLoading)
                    ]
                  )}
                </Button>
                <Button
                  type="submit"
                  variant="grayBtn"
                  size="sx"
                  onClick={() => {
                    setOpen(!open)
                  }}
                >
                  {t('CANCEL')}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
      <ScreenLoader isLoadingScreen={isLoading && open} />
    </>
  )
}

export default GuestNewsModal
